// Renders a chess position to a canvas for the export kit PNGs. Pieces come
// from the same cm-chessboard SVG sprites as the live overlay: each <g id="wk">
// tile is wrapped into a standalone SVG (with any shared <defs> inlined) and
// rasterized via a Blob URL, so the canvas is never tainted.

const PIECE_IDS = ['wk', 'wq', 'wr', 'wb', 'wn', 'wp', 'bk', 'bq', 'br', 'bb', 'bn', 'bp']
const spriteCache = new Map()

export function loadPieceImages(spriteUrl) {
  if (!spriteCache.has(spriteUrl)) {
    spriteCache.set(
      spriteUrl,
      (async () => {
        const res = await fetch(spriteUrl)
        if (!res.ok) throw new Error(`Couldn't load the piece sprite (HTTP ${res.status})`)
        const doc = new DOMParser().parseFromString(await res.text(), 'image/svg+xml')
        const defs = [...doc.querySelectorAll('defs')].map((d) => d.innerHTML).join('')
        const entries = await Promise.all(
          PIECE_IDS.map(async (id) => {
            const node = doc.getElementById(id)
            if (!node) return [id, null]
            const svg =
              `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">` +
              (defs ? `<defs>${defs}</defs>` : '') +
              node.outerHTML +
              `</svg>`
            return [id, await svgToImage(svg)]
          }),
        )
        return Object.fromEntries(entries)
      })(),
    )
  }
  return spriteCache.get(spriteUrl)
}

function svgToImage(svgText) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([svgText], { type: 'image/svg+xml' }))
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Couldn’t rasterize a piece from the sprite'))
    }
    img.src = url
  })
}

function fenToGrid(fen) {
  // grid[r][c] from White's view: r 0 = rank 8, c 0 = file a.
  const rows = String(fen).split(' ')[0].split('/')
  return rows.map((row) => {
    const cells = []
    for (const ch of row) {
      if (/\d/.test(ch)) cells.push(...Array(Number(ch)).fill(null))
      else cells.push(ch)
    }
    return cells
  })
}

const squareName = (r, c) => 'abcdefgh'[c] + (8 - r)

export async function renderBoardCanvas({
  fen,
  sizePx,
  theme,
  pieceImages,
  lastMove = null,
  orientation = 'white',
  coords = false,
  opacity = 1,
}) {
  const square = sizePx / 8
  const canvas = document.createElement('canvas')
  canvas.width = sizePx
  canvas.height = sizePx
  const ctx = canvas.getContext('2d')
  const grid = fenToGrid(fen)
  const flipped = orientation === 'black'
  const highlight = new Set(lastMove ? [lastMove.from, lastMove.to] : [])

  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const dr = flipped ? 7 - r : r
      const dc = flipped ? 7 - c : c
      const x = dc * square
      const y = dr * square
      ctx.fillStyle = (r + c) % 2 === 0 ? theme.light : theme.dark
      ctx.fillRect(x, y, square + 1, square + 1)
      if (highlight.has(squareName(r, c))) {
        ctx.fillStyle = 'rgba(255, 206, 70, 0.4)' // matches the live marker CSS
        ctx.fillRect(x, y, square + 1, square + 1)
      }
      const piece = grid[r][c]
      if (piece) {
        const id = (piece === piece.toUpperCase() ? 'w' : 'b') + piece.toLowerCase()
        const img = pieceImages[id]
        if (img) ctx.drawImage(img, x, y, square, square)
      }
    }
  }

  if (coords) {
    ctx.font = `500 ${Math.max(8, Math.round(square * 0.17))}px 'IBM Plex Sans', sans-serif`
    const light = 'rgba(255,255,255,0.65)'
    const dark = 'rgba(0,0,0,0.55)'
    for (let dc = 0; dc < 8; dc++) {
      const file = flipped ? 'hgfedcba'[dc] : 'abcdefgh'[dc]
      ctx.fillStyle = (7 + dc) % 2 === 0 ? dark : light
      ctx.textAlign = 'right'
      ctx.textBaseline = 'alphabetic'
      ctx.fillText(file, (dc + 1) * square - square * 0.08, sizePx - square * 0.08)
    }
    for (let dr = 0; dr < 8; dr++) {
      const rank = flipped ? String(dr + 1) : String(8 - dr)
      ctx.fillStyle = dr % 2 === 0 ? dark : light
      ctx.textAlign = 'left'
      ctx.textBaseline = 'hanging'
      ctx.fillText(rank, square * 0.08, dr * square + square * 0.08)
    }
  }

  if (opacity < 1) {
    const composite = document.createElement('canvas')
    composite.width = sizePx
    composite.height = sizePx
    const cctx = composite.getContext('2d')
    cctx.globalAlpha = opacity
    cctx.drawImage(canvas, 0, 0)
    return composite
  }
  return canvas
}

export function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('PNG encoding failed'))), 'image/png')
  })
}
